import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withRouter } from 'react-router';

import { EvSection, EvTitle, useSkipLinks } from '@evinced-private/ui-common';

import {
	PROPERTIES_PAGE_TABLE_ID,
	PropertiesTableV2
} from '../../components/properties-table/PropertiesTableV2';
import { ApiErrorHelper } from '../../helpers/ApiErrorHelper';
import { useGetAllProperties } from '../../hooks/queries/useGetAllProperties';
import { IServerError } from '../../interfaces/ServerError';
import { useCollections } from '../../providers/collections/CollectionsProvider';
import { useCommonFiltersStore } from '../../providers/commonFiltersProvider/CommonFiltersProvider';
import { useNotifications } from '../../providers/notificationsProvider/NotificationsConsumer';
import { useTablesStore } from '../../providers/tablesProvider/TablesProvider';
import { logger } from '../../services/Logger';

import { PropertiesPageActions } from './components/properties-page-actions/PropertiesPageActions';
import { PropertiesPageFilters } from './components/properties-page-filters/PropertiesPageFilters';
import {
	defaultFilterValues,
	PROPERTIES_PAGE_FILTERS_ID
} from './helpers/PropertiesPageFiltersHelper';
import { LABELS } from './PropertiesPage.constants';

import './PropertiesPage.scss';

const PropertiesPageV2: FC = () => {
	const tableSectionRef = useRef<HTMLDivElement>(null);
	const commonFiltersStore = useCommonFiltersStore();
	const tablesStore = useTablesStore();
	const { showOnlyPropertiesInMyCollection } = useCollections();
	const notificationsContext = useNotifications();

	const { sizePerPage } = tablesStore.getPagination(PROPERTIES_PAGE_TABLE_ID);
	const [resetKey, setResetKey] = useState(0);
	const [runGetPropertiesInterval, setRunGetPropertiesInterval] = useState(true);

	const appliedFilters = commonFiltersStore.getFilterValue(PROPERTIES_PAGE_FILTERS_ID);

	const filterCollectionIds = useMemo(
		() => appliedFilters?.collections?.map(({ value }) => value as string) || null,
		[appliedFilters]
	);

	// updating skip links when page loads
	useSkipLinks(true);

	const {
		data: properties = [],
		isLoading,
		isError,
		error,
		refetch
	} = useGetAllProperties(
		filterCollectionIds,
		showOnlyPropertiesInMyCollection,
		runGetPropertiesInterval
	);

	const handleResetFilters = useCallback(() => {
		commonFiltersStore.updateSelectedFilters(PROPERTIES_PAGE_FILTERS_ID, defaultFilterValues);
		tablesStore.updatePagination(PROPERTIES_PAGE_TABLE_ID, { page: 1, sizePerPage });
		setResetKey((prev) => prev + 1);
	}, [commonFiltersStore, sizePerPage, tablesStore]);

	useEffect(() => {
		if (!isError) {
			return;
		}

		const errMessage = ApiErrorHelper.getErrorMessage(error as unknown as IServerError);
		const msg = `An error occurred while trying to load properties list.\n Message: ${errMessage}`;
		logger.error(msg, error);

		notificationsContext.alert({
			title: 'Error loading properties list',
			serverError: error as unknown as IServerError
		});

		setRunGetPropertiesInterval(false);
	}, [error, isError, notificationsContext]);

	return (
		<div className="properties-page">
			<EvTitle titleText="all properties page" id="main-title" className="sr-only" />
			<div className="properties-page-header">
				<PropertiesPageFilters
					handleResetFilters={handleResetFilters}
					resetKey={resetKey}
					focusTargetRef={tableSectionRef}
				/>
				<PropertiesPageActions />
			</div>

			<EvSection skipLinkId="properties-table" ariaLabel="Properties table">
				<div ref={tableSectionRef} tabIndex={-1} className="sr-only">
					{LABELS.TABLE_SECTION_SR_LABEL}
				</div>
				<PropertiesTableV2
					properties={properties}
					isLoading={isLoading}
					refetch={refetch}
					handleResetFilters={handleResetFilters}
				/>
			</EvSection>
		</div>
	);
};

export const PropertiesPageV2WithRouter = withRouter(PropertiesPageV2);
